import type { OperationItem } from './types';

type OperatorRef = { id?: string | null };

type ConflictItem = Pick<
  OperationItem,
  'id' | 'startTime' | 'endTime' | 'tourOperators'
>;

/**
 * Item ids whose tour-operator assignments overlap in time with another item's
 * — i.e. the same operator is booked for two items whose [start, end) windows
 * intersect. Computed across the currently-visible page (board scope), mirroring
 * the server's `getConflictItemIds` (operations-tour-operator.ts). String time
 * comparison is valid because the API returns ISO 8601 timestamps.
 */
export const computeConflictingItemIds = (
  items: ConflictItem[]
): Set<string> => {
  const slotsByOperator = new Map<
    string,
    { start: string; end: string; itemId: string }[]
  >();

  for (const item of items) {
    if (!item.id || !item.startTime || !item.endTime) continue;
    const operators = Array.isArray(item.tourOperators)
      ? (item.tourOperators as OperatorRef[])
      : [];
    for (const op of operators) {
      if (!op?.id) continue;
      const slots = slotsByOperator.get(op.id) ?? [];
      slots.push({ start: item.startTime, end: item.endTime, itemId: item.id });
      slotsByOperator.set(op.id, slots);
    }
  }

  const conflicts = new Set<string>();
  for (const slots of slotsByOperator.values()) {
    for (let i = 0; i < slots.length; i++) {
      for (let j = i + 1; j < slots.length; j++) {
        const a = slots[i];
        const b = slots[j];
        if (a.start < b.end && b.start < a.end) {
          conflicts.add(a.itemId);
          conflicts.add(b.itemId);
        }
      }
    }
  }
  return conflicts;
};
